import { PageableInput, SIDE } from '@allocab/allocab-commons-shared-types';
import { FindDedicatedDriverPreferencesInput } from '@allocab/allocab-dedicated-driver-types';
import {
  DriverInvoiceBySepaTransferQueryInput,
  DriverSearchInvoiceInput,
  NetGainFilterInput,
  SearchTripManagerInput,
  TransactionQueryInput,
} from '@allocab/allocab-driver-shared-types';

import { InvoiceResumeInput } from '../../hooks/types';

export const queryKeys = {
  tokens: () => ['tokens'],
  user: {
    root: () => ['user'],
    me: () => [...queryKeys.user.root(), 'me'],
    fetchIntercom: () => [...queryKeys.user.root(), 'fetchIntercom'],
    fetchVehicle: (id?: string) => [...queryKeys.user.root(), 'fetchVehicle', ...(id ? [id] : [])],
    fetchDriver: (id: string) => [...queryKeys.user.root(), 'fetchDriver', id],
    fetchDrivePreferencesById: (id: string) => [...queryKeys.user.root(), 'fetchDrivePreferencesById', id],
    fetchDriversPreferencesData: (input?: FindDedicatedDriverPreferencesInput) => [
      'fetchDriversPreferencesData',
      ...(input ? [input] : []),
    ],
    fetchTransaction: (id: string) => [...queryKeys.user.root(), 'fetchTransaction', id],
    fetchNetGainsByDriver: (input?: NetGainFilterInput) => [
      ...queryKeys.user.root(),
      'fetchNetGainsByDriver',
      ...(input ? [input] : []),
    ],
    fetchNetGains: (input?: NetGainFilterInput) => [
      ...queryKeys.user.root(),
      'fetchNetGains',
      ...(input ? [input] : []),
    ],
    fetchIncomingTransactions: () => [...queryKeys.user.root(), 'fetchIncomingTransactions'],
    fetchTransactions: (input?: Omit<TransactionQueryInput, 'offset' | 'limit'>) => [
      ...queryKeys.user.root(),
      'fetchTransactions',
      ...(input ? [input] : []),
    ],
    fetchTransactionInvoices: (input?: Omit<DriverInvoiceBySepaTransferQueryInput, 'offset' | 'limit'>) => [
      ...queryKeys.user.root(),
      'fetchTransactionInvoices',
      ...(input ? [input] : []),
    ],
    fetchAccount: () => [...queryKeys.user.root(), 'fetchAccount'],
    fetchDrivers: (input?: PageableInput) => [...queryKeys.user.root(), 'fetchDrivers', ...(input ? [input] : [])],
    fetchInfiniteDrivers: (input?: Omit<PageableInput, 'offset' | 'limit'>) => [
      ...queryKeys.user.root(),
      'fetchInfiniteDrivers',
      ...(input ? [input] : []),
    ],
    fetchInvoices: (input?: DriverSearchInvoiceInput) => [
      ...queryKeys.user.root(),
      'fetchInvoices',
      ...(input ? [input] : []),
    ],
    fetchDriversForVehicle: (vehicleId: string, input?: PageableInput) => [
      ...queryKeys.user.root(),
      'fetchDriversForVehicle',
      vehicleId,
      ...(input ? [input] : []),
    ],
    fetchVehicles: (input?: PageableInput) => [...queryKeys.user.root(), 'fetchVehicles', ...(input ? [input] : [])],
    fetchInfiniteVehicles: (input?: Omit<PageableInput, 'offset' | 'limit'>) => [
      ...queryKeys.user.root(),
      'fetchInfiniteVehicles',
      ...(input ? [input] : []),
    ],
    fetchVehiclesForDriver: (driverId: string, input?: PageableInput) => [
      ...queryKeys.user.root(),
      'fetchVehiclesForDriver',
      driverId,
      ...(input ? [input] : []),
    ],
    fetchInfiniteVehiclesForDriver: (input?: Omit<PageableInput, 'offset' | 'limit'>) => [
      ...queryKeys.user.root(),
      'fetchInfiniteVehiclesForDriver',
      ...(input ? [input] : []),
    ],
    fetchUserSubscription: () => [...queryKeys.user.root(), 'fetchUserSubscription'],
    fetchDriverOptions: () => [...queryKeys.user.root(), 'fetchDriverOptions'],
    fetchTrips: (input?: SearchTripManagerInput) => [...queryKeys.user.root(), 'fetchTrips', ...(input ? [input] : [])],
    fetchInfiniteTrips: (input?: Omit<SearchTripManagerInput, 'offset' | 'limit'>) => [
      ...queryKeys.user.root(),
      'fetchInfiniteTrips',
      ...(input ? [input] : []),
    ],
    fetchTripPackages: (input?: PageableInput) => [
      ...queryKeys.user.root(),
      'fetchTripPackages',
      ...(input ? [input] : []),
    ],
    fetchInfiniteTripPackages: (input?: Omit<PageableInput, 'offset' | 'limit'>) => [
      ...queryKeys.user.root(),
      'fetchInfiniteTripPackages',
      ...(input ? [input] : []),
    ],
    fetchTripPackagesByDriverKey: (id?: string, input?: PageableInput) => [
      ...queryKeys.user.root(),
      'fetchTripPackagesByDriverKey',
      ...(id ? [id] : []),
      ...(input ? [input] : []),
    ],
    fetchInfiniteTripPackagesByDriverKey: (id?: string, input?: Omit<PageableInput, 'offset' | 'limit'>) => [
      ...queryKeys.user.root(),
      'fetchInfiniteTripPackagesByDriverKey',
      ...(id ? [id] : []),
      ...(input ? [input] : []),
    ],
    fetchTrip: (key?: string) => [...queryKeys.user.root(), 'fetchTrip', ...(key ? [key] : [])],
    fetchVehicleDocuments: (id?: string) => [...queryKeys.user.root(), 'fetchVehicleDocuments', ...(id ? [id] : [])],
    fetchDriverDocuments: (id?: string) => [...queryKeys.user.root(), 'fetchDriverDocuments', ...(id ? [id] : [])],
    fetchSuggestions: (searchQueryParam: string | null) => [
      ...queryKeys.user.root(),
      'fetchSuggestions',
      searchQueryParam,
    ],
    fetchAccountDocuments: () => [...queryKeys.user.root(), 'fetchAccountDocuments'],
    fetchInvoiceResume: (input?: InvoiceResumeInput) => [
      ...queryKeys.user.root(),
      'fetchInvoiceResume',
      ...(input ? [input] : []),
    ],
    fetchInvoiceResumeByMonth: (date?: string) => [...queryKeys.user.root(), 'fetchInvoiceResumeByMonth', date],
    fetchDocumentLink: (documentId: string | undefined, input: { side: SIDE }) => [
      ...queryKeys.user.root(),
      'fetchDocumentLink',
      documentId,
      input,
    ],
    searchTrips: (input?: Omit<SearchTripManagerInput, 'offset' | 'limit'>) => [
      ...queryKeys.user.root(),
      'searchTrips',
      ...(input ? [input] : []),
    ],
    fetchMerchantAccountStatus: () => [...queryKeys.user.root(), 'fetchMerchantAccountStatus'],
  },
};
